import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { getUserEmail } from '../auth';
import { errMessage } from '../pageUtils';
import { MapBoundary } from '../components/SiteMapView';
import { CONFIDENCE_CLASS } from '../components/SiteLocationNotes';
import { effectiveLocationConfidence } from '../api/siteConfidence';
import { overlayCorners, renderPlanImage } from '../api/planOverlay';
import {
  adjustOutline, calculatedOutline, withOrigin, ringCentre, ringRadiusM as coveringRadiusM, shiftRing, shiftParts, NO_ADJUST, MIN_SCALE, MAX_SCALE,
  type OutlineAdjust,
} from '../api/outlineGeometry';
import {
  locateSite, locateBoundary, saveSiteLocation, parseSavedLocation,
  type LocateResult, type SiteConstraint, type Confidence, type SiteBoundary, type SiteLocationInput,
} from '../api/locate';

import PreAppLocateStep from './PreAppLocateStep';

const LocateMap = lazy(() => import('../components/LocateMap'));

interface Position { lat: number; lon: number }

const MIN_RADIUS = 10;
const MAX_RADIUS = 2000;
const NO_LAYERS: string[] = [];
const noop = () => undefined;

type Ring = [number, number][];

/** Radius of the circle around the centre that covers the whole outline, within the slider's range. */
function ringRadiusM(ring: Ring, c: Position): number {
  return coveringRadiusM(ring, c, MIN_RADIUS, MAX_RADIUS);
}

function outlineNote(tier: Confidence): string {
  return `The pin is the centre of the site outline matched to the map, so the confidence shown is that of the outline (${tier}).`;
}

const PIN_SEEDED_LINE = 'The site outline was found on the location plan starting from a pin placed by the user.';

const clampRadius = (r: number) => Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, Math.round(r)));

// Lines saved because of what the user did; they are rebuilt from the current state on every save, so never carried over.
const USER_LINE = /^(The site outline was .+ by the user|The location was set by the user|The user (resized|turned))/;

/** The saved location in the shape a fresh locate returns, so the page can show and re-save it without a new search. */
function resultFromSaved(s: SiteLocationInput): LocateResult {
  return {
    lat: s.lat, lon: s.lon, radiusM: s.radiusM, confidence: s.confidence,
    siteName: s.siteName ?? null, summary: s.summary ?? null, siteAreaHectares: null, streets: [], siteAddress: null,
    basis: s.evidence.filter(l => !USER_LINE.test(l)), quotes: [], warnings: s.warnings, constraints: s.constraints,
    gazetteerVersion: s.gazetteerVersion ?? null, method: s.source, alternatives: [], usage: s.usage ?? null,
  };
}

function adjustLine(a: OutlineAdjust): string {
  const parts: string[] = [];
  if (Math.abs(a.scale - 1) > 1e-6) parts.push(`resized the outline to ${Math.round(a.scale * 100)}% of the size read from the plan`);
  if (Math.abs(a.rotationDeg) > 1e-6) parts.push(`turned it ${Math.abs(a.rotationDeg).toFixed(1)}° ${a.rotationDeg > 0 ? 'clockwise' : 'anticlockwise'}`);
  return `The user ${parts.join(' and ')}.`;
}

/** A number box that takes what is typed as is and applies it on Enter or when focus leaves, so half-typed values are not clamped away. */
function NumberField({ value, label, min, max, step, disabled, onCommit }: {
  value: number; label: string; min: number; max: number; step: number; disabled: boolean; onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  function commit() {
    if (draft == null) return;
    const v = Number(draft);
    setDraft(null);
    if (draft.trim() !== '' && Number.isFinite(v) && v !== value) onCommit(v);
  }
  return (
    <input
      type="number" className="sx-input lm-num" aria-label={label} min={min} max={max} step={step} disabled={disabled}
      value={draft ?? String(value)}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } }}
    />
  );
}

interface LocateStepProps {
  caseId: string; onDone: (info?: { namedAuthority?: string | null }) => void; saved?: string | null; onCancel?: () => void; cancelling?: boolean;
  /** A pre-application check has no documents to find the site from, so the applicant marks it themselves. */
  preApplication?: boolean;
  /** Shaded on a pre-application check's map while zoomed out: the local planning authorities we cover. */
  coveredAuthorities?: string[];
}

// onCancel, when given, offers a Cancel button on every screen of the step (the caller decides what cancelling means).
export default function LocateStep({ preApplication = false, ...props }: LocateStepProps) {
  return preApplication ? <PreAppLocateStep {...props} onDone={() => props.onDone()} /> : <DocumentLocateStep {...props} />;
}

function DocumentLocateStep({ caseId, onDone, saved, onCancel, cancelling = false }: Omit<LocateStepProps, 'preApplication'>) {
  // Relocating starts from what is already saved; a first locate has nothing saved and searches straight away.
  const [savedStart] = useState(() => {
    const s = parseSavedLocation(saved);
    return s?.boundary ? { ...s, boundary: s.boundary.userEdited ? s.boundary : withOrigin(s.boundary) } : s;
  });
  const [mode, setMode] = useState<'saved' | 'locate'>(savedStart ? 'saved' : 'locate');
  const [touched, setTouched] = useState(!savedStart);
  const [result, setResult] = useState<LocateResult | null>(() => (savedStart ? resultFromSaved(savedStart) : null));
  const [locating, setLocating] = useState(!savedStart);
  const [locateError, setLocateError] = useState<string | null>(null);

  const [pos, setPos] = useState<Position | null>(savedStart ? { lat: savedStart.lat, lon: savedStart.lon } : null);
  const [radiusM, setRadiusM] = useState(savedStart ? clampRadius(savedStart.radiusM) : 250);
  const [constraints, setConstraints] = useState<SiteConstraint[]>(savedStart?.constraints ?? []);
  const [edited, setEdited] = useState(savedStart?.userEdited ?? false);
  const [fitToken, setFitToken] = useState(savedStart ? 1 : 0);
  const [rotationUnlocked, setRotationUnlocked] = useState(false);
  const [boundary, setBoundary] = useState<SiteBoundary | null>(savedStart?.boundary ?? null);
  const [boundaryBusy, setBoundaryBusy] = useState(false);
  const [boundaryNote, setBoundaryNote] = useState<string | null>(null);
  // Set from locateBoundary's onProgress while a match is in flight; null once it settles, so the bar never lingers.
  const [boundaryProgress, setBoundaryProgress] = useState<number | null>(null);
  const [boundaryStage, setBoundaryStage] = useState<string | null>(null);
  // The outline as read from the plan, kept so the user can go back to it after choosing a pin.
  const [readBoundary, setReadBoundary] = useState<SiteBoundary | null>(() => (savedStart?.boundary ? calculatedOutline(savedStart.boundary) : null));
  // Every place the outline might sit, the most likely first; only offered while the outline is as read off the plan.
  const [options, setOptions] = useState<SiteBoundary[]>([]);
  const [optionIdx, setOptionIdx] = useState(0);
  const editedRef = useRef(false);
  editedRef.current = edited;

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pinSearchSeq = useRef(0);
  useEffect(() => () => { pinSearchSeq.current++; }, []);
  // The outline was found by searching from a pin the user placed, so the earlier "could not find the site" notes no longer apply.
  const [pinSeeded, setPinSeeded] = useState(false);

  const [showPlan, setShowPlan] = useState(true);
  const [planOpacity, setPlanOpacity] = useState(0.35);
  const [planUrl, setPlanUrl] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const plan = boundary?.plan ?? null;
  const planKey = plan ? `${plan.documentId}|${plan.page}|${plan.frame.join(',')}` : '';

  useEffect(() => {
    if (!plan || !showPlan) return;
    let cancelled = false;
    setPlanLoading(true); setPlanError(null); setPlanUrl(null);
    renderPlanImage(plan.documentId, plan.page, plan.frame)
      .then(u => { if (!cancelled) setPlanUrl(u); })
      .catch((e: Error) => { if (!cancelled) setPlanError(e.message); })
      .finally(() => { if (!cancelled) setPlanLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, showPlan]);

  const planCorners = boundary ? overlayCorners(boundary) : null;
  const planImage = showPlan && plan && planUrl && planCorners ? { url: planUrl, corners: planCorners, opacity: planOpacity } : null;

  useEffect(() => {
    if (mode !== 'locate') return;
    let cancelled = false;
    setLocating(true); setLocateError(null); setBoundaryBusy(false);
    locateSite(caseId)
      .then(r => {
        if (cancelled) return;
        setBoundaryBusy(true);
        setResult(r);
        setConstraints(r.constraints);
        setRadiusM(clampRadius(r.radiusM));
        if (r.lat != null && r.lon != null) {
          setPos({ lat: r.lat, lon: r.lon });
          setFitToken(t => t + 1);
        }
      })
      .catch((e: Error) => { if (!cancelled) setLocateError(e.message); })
      .finally(() => { if (!cancelled) setLocating(false); });
    return () => { cancelled = true; };
  }, [caseId, mode]);

  // A Location Plan, when the case has one, replaces the pin and radius with the site's outline. Without one the pin flow is unchanged.
  const located = result != null;
  useEffect(() => {
    if (mode !== 'locate' || !located || !result) return;
    let cancelled = false;
    setBoundaryBusy(true); setBoundaryNote(null); setBoundaryProgress(null); setBoundaryStage(null);
    const seed = result.lat != null && result.lon != null ? { lat: result.lat, lon: result.lon, confidence: result.confidence } : null;
    locateBoundary(caseId, seed, result.siteAreaHectares, result.streets, (pct, stage) => {
      if (!cancelled) { setBoundaryProgress(pct); setBoundaryStage(stage); }
    }, result.siteAddress, result.siteName)
      .then(r => {
        if (cancelled) return;
        if (!r.boundary) { setBoundaryNote(r.warnings[0] ?? null); return; }
        const read = withOrigin(r.boundary);
        setReadBoundary(read);
        setOptions([read, ...r.candidates.map(c => withOrigin(c.boundary))]); setOptionIdx(0);
        if (editedRef.current) { setBoundaryNote('A site outline was found on the location plan, but you had already moved the pin, so it was not applied. You can still use it with the button under the map.'); return; }
        const centre = ringCentre(read.ring);
        setBoundary(read);
        setPos(centre);
        setRadiusM(ringRadiusM(read.ring, centre));
        setFitToken(t => t + 1);
      })
      .catch((e: Error) => { if (!cancelled) setBoundaryNote(`The location plan could not be read (${e.message}). The pin and search area below are still available.`); })
      .finally(() => { if (!cancelled) { setBoundaryBusy(false); setBoundaryProgress(null); setBoundaryStage(null); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, located, mode]);

  function restoreSaved() {
    if (!savedStart) return;
    pinSearchSeq.current++; setPinSeeded(false);
    setMode('saved'); setTouched(false); setResult(resultFromSaved(savedStart)); setLocating(false); setLocateError(null);
    setBoundaryBusy(false); setBoundaryNote(null); setReadBoundary(savedStart.boundary ? calculatedOutline(savedStart.boundary) : null); setSaveError(null);
    setPos({ lat: savedStart.lat, lon: savedStart.lon }); setRadiusM(clampRadius(savedStart.radiusM));
    setConstraints(savedStart.constraints); setEdited(savedStart.userEdited); setBoundary(savedStart.boundary ?? null);
    setRotationUnlocked(false); setFitToken(t => t + 1);
  }

  function findAgain() {
    pinSearchSeq.current++; setPinSeeded(false);
    setMode('locate'); setTouched(true); setResult(null); setLocating(true); setLocateError(null);
    setBoundary(null); setReadBoundary(null); setBoundaryNote(null); setPos(null); setConstraints([]); setEdited(false);
    setSaveError(null); setRotationUnlocked(false);
  }

  function applyAdjust(next: OutlineAdjust) {
    if (!boundary || !pos) return;
    const b = adjustOutline(boundary, next);
    setSaveError(null); setTouched(true); setEdited(true);
    setBoundary({ ...b, userEdited: true });
    setRadiusM(ringRadiusM(b.ring, pos));
  }

  function pick(p: Position) {
    setSaveError(null); setTouched(true);
    if (boundary && pos) {
      // The outline keeps its shape and moves with the pin.
      setBoundary({ ...boundary, ring: shiftRing(boundary.ring, p.lon - pos.lon, p.lat - pos.lat), ...(boundary.parts ? { parts: shiftParts(boundary.parts, p.lon - pos.lon, p.lat - pos.lat) } : {}), userEdited: true });
    }
    setPos(p); setEdited(true);
  }

  // An explicit request, so unlike the automatic search it replaces a pin the user has placed. Sent as a pin, so the search stays close to it.
  function searchFromPin(start: Position | null = pos) {
    if (!start) return;
    const seq = ++pinSearchSeq.current;
    const from = { lat: start.lat, lon: start.lon };
    setBoundaryBusy(true); setBoundaryNote(null); setBoundaryProgress(null); setBoundaryStage(null); setSaveError(null);
    locateBoundary(caseId, { ...from, confidence: 'Medium', pin: true }, result?.siteAreaHectares ?? null, result?.streets ?? [], (pct, stage) => {
      if (seq === pinSearchSeq.current) { setBoundaryProgress(pct); setBoundaryStage(stage); }
    }, result?.siteAddress, result?.siteName)
      .then(r => {
        if (seq !== pinSearchSeq.current) return;
        if (!r.boundary) { setBoundaryNote(r.warnings[0] ?? 'No site outline could be found on the location plan near the pin. Try moving the pin closer to the site.'); return; }
        const read = withOrigin(r.boundary);
        const centre = ringCentre(read.ring);
        setReadBoundary(read); setBoundary(read); setPos(centre); setRadiusM(ringRadiusM(read.ring, centre));
        setOptions([read, ...r.candidates.map(c => withOrigin(c.boundary))]); setOptionIdx(0);
        setEdited(false); setPinSeeded(true); setTouched(true); setFitToken(t => t + 1);
      })
      .catch((e: Error) => { if (seq === pinSearchSeq.current) setBoundaryNote(`The location plan could not be read (${e.message}). Your pin is unchanged.`); })
      .finally(() => { if (seq === pinSearchSeq.current) { setBoundaryBusy(false); setBoundaryProgress(null); setBoundaryStage(null); } });
  }

  function chooseOption(i: number) {
    const next = options[i];
    if (!next) return;
    const centre = ringCentre(next.ring);
    setSaveError(null); setBoundaryNote(null); setTouched(true);
    setOptionIdx(i); setReadBoundary(next); setBoundary(next);
    setPos(centre); setRadiusM(ringRadiusM(next.ring, centre));
    setEdited(false); setFitToken(t => t + 1);
  }

  function useReadBoundary() {
    if (!readBoundary) return;
    const centre = ringCentre(readBoundary.ring);
    setSaveError(null); setBoundaryNote(null); setTouched(true);
    setBoundary(readBoundary);
    setPos(centre);
    setRadiusM(ringRadiusM(readBoundary.ring, centre));
    setEdited(false);
    setFitToken(t => t + 1);
  }

  function discardBoundary() {
    setBoundary(null); setBoundaryNote(null); setTouched(true); setPinSeeded(false);
   
    if (!result) return;
    setConstraints(result.constraints);
    setRadiusM(clampRadius(result.radiusM));
    // The saved designations were checked against the outline, so a pin chosen over it needs a fresh lookup.
    setEdited(mode === 'saved');
    if (result.lat != null && result.lon != null) { setPos({ lat: result.lat, lon: result.lon }); setFitToken(t => t + 1); } else setPos(null);
  }

  // A saved location already carries this note in its own evidence, so it is only added to a fresh search result.
  const outlinePlaced = mode === 'locate' && !edited && boundary != null && boundary.placement === 'matched';

  async function confirm() {
    if (!pos) return;
    setSaving(true); setSaveError(null);
    try {
      const evidence = edited
        ? [
          boundary ? 'The site outline was adjusted by the user on the map.' : 'The location was set by the user on the map.',
          ...(boundary?.adjust ? [adjustLine(boundary.adjust)] : []),
          ...(result?.basis ?? []),
        ]
        : pinSeeded
          ? [PIN_SEEDED_LINE, ...(outlinePlaced ? [outlineNote(boundary.confidence)] : [])]
          : [...(outlinePlaced ? [outlineNote(boundary.confidence)] : []), ...(result?.basis ?? []), ...(result?.quotes ?? []).map(q => `The documents say: "${q}"`)];
      await saveSiteLocation(caseId, {
        lat: pos.lat, lon: pos.lon, radiusM,
        confidence: edited ? 'High' : effectiveLocationConfidence({ userEdited: false, locate: pinSeeded ? 'Low' : (result?.confidence ?? 'Low'), boundary }),
        source: edited ? 'user' : (result?.method ?? 'locate'),
        // Designations are looked up when the case is assessed; any from the first locate no longer fit a site moved since.
        evidence, warnings: edited || pinSeeded ? [] : (result?.warnings ?? []), constraints: edited ? [] : constraints,
        ...(result?.summary ? { summary: result.summary } : {}),
        ...(result?.siteName ? { siteName: result.siteName } : {}),
        gazetteerVersion: result?.gazetteerVersion ?? undefined,
        usage: result?.usage ?? undefined,
        userEdited: edited,
        ...(boundary ? { boundary } : {}),
      }, getUserEmail() ?? undefined);
      onDone({ namedAuthority: result?.namedAuthority ?? null });
    } catch (e) {
      setSaveError(errMessage(e, 'The location could not be saved.'));
      setSaving(false);
    }
  }

  const cancelButton = onCancel && (
    <button type="button" className="sx-cta sx-cta-2" disabled={cancelling || saving} onClick={onCancel}>
      {cancelling ? 'Cancelling…' : 'Cancel'}
    </button>
  );

  // The question is only asked once there is an answer to check. Until the boundary search settles the map is shown as a read-only preview.
  if (locating) {
    return (
      <div className="sx-progress" role="status" aria-live="polite">
        <div className="sx-spinner" />
        <h2>Finding the site…</h2>
        <p className="sx-note">Reading your documents for the site address, then checking nearby planning designations. This can take a minute or two.</p>
        {onCancel && <div style={{ width: '100%', maxWidth: 260 }}>{cancelButton}</div>}
      </div>
    );
  }
  if (boundaryBusy) {
    return (
      <div className="lm-step">
        <div role="status" aria-live="polite">
          <h1 className="sx-h1">Looking for the site boundary…</h1>
          <p className="sx-lede">{boundaryStage ?? 'Searching your location plan for the outline of the site.'} This can take a minute or two.</p>
          {boundaryProgress != null && (
            <div className="sx-progress-bar" aria-hidden="true"><div className="sx-progress-bar-fill" style={{ width: `${boundaryProgress}%` }} /></div>
          )}
        </div>
        <div className="lm-map-pending" aria-hidden="true">
          <MapBoundary failMessage="The map could not be loaded.">
            <Suspense fallback={<div className="lm-map lm-map-off"><span className="skel" /></div>}>
              <LocateMap position={pos} radiusM={radiusM} boundary={null} fitToken={fitToken} onPick={noop} layers={NO_LAYERS} onLayerNote={noop} readOnly />
            </Suspense>
          </MapBoundary>
        </div>
        {cancelButton}
      </div>
    );
  }

  const confidence: Confidence = effectiveLocationConfidence({ userEdited: edited, locate: pinSeeded ? 'Low' : (result?.confidence ?? 'Not established'), boundary });
  const basis = edited ? [] : pinSeeded
    ? [PIN_SEEDED_LINE, ...(outlinePlaced ? [outlineNote(boundary.confidence)] : [])]
    : [...(outlinePlaced ? [outlineNote(boundary.confidence)] : []), ...(result?.basis ?? [])];
  const quotes = edited || pinSeeded ? [] : (result?.quotes ?? []);
  const warnings = edited || pinSeeded ? [] : (result?.warnings ?? []);

  const showOptions = !!boundary && options.length > 1 && !edited && !boundary.userEdited && mode === 'locate';

  const locationHead = (
    <div className="lm-sum">
      <h2 className="sx-sec-h">How we found the location</h2>
      <span className={`sx-chip ${edited ? 'sx-chip-pos' : CONFIDENCE_CLASS[confidence]}`}>
        {edited ? 'Set by you' : confidence === 'Not established' ? 'Location not established' : `${confidence} confidence`}
      </span>
      {result?.siteName && !edited && <span className="lm-site-name">{result.siteName}</span>}
    </div>
  );

  return (
    <div className="lm-step">
      <div>
        <h1 className="sx-h1">{mode === 'saved' ? 'Correct the site location' : 'Is this the site?'}</h1>
        <p className="sx-lede">{boundary
          ? `${mode === 'saved' ? 'This is the site outline saved for this case.' : 'We read the site outline from your location plan.'} Check it sits on the application site. Drag the pin, or click the map, to move the whole outline, and use the size and rotation controls below to correct it. We use it to look up designations such as Green Belt and flood risk.`
          : 'Check the pin is on the application site. Drag it, or click the map, to correct it. We use this to look up designations such as Green Belt and flood risk.'}</p>
      </div>

      {locateError && (
        <div className="sx-err">
          We could not work out the location automatically ({locateError}). Place the pin yourself.
          {savedStart && <>{' '}<button type="button" className="sx-cta sx-cta-2" onClick={restoreSaved}>Go back to the saved location</button></>}
        </div>
      )}

      <div className="lm-map-wrap">
        <div>
          <MapBoundary failMessage="The map could not be loaded.">
            <Suspense fallback={<div className="lm-map lm-map-off"><span className="skel" /></div>}>
              <LocateMap position={pos} radiusM={radiusM} boundary={boundary?.ring ?? null} extraRings={boundary?.parts} planImage={planImage} fitToken={fitToken} onPick={pick} layers={NO_LAYERS} onLayerNote={noop} />
            </Suspense>
          </MapBoundary>
        </div>
      </div>

      {!boundary && edited && pos && (
        <div className="lm-map-actions">
          <button type="button" className="sx-cta" disabled={saving} onClick={() => searchFromPin()}>Find the outline from here</button>
          <div className="sx-note">Searches your location plan for the site outline around the pin. Move the pin onto the site first.</div>
        </div>
      )}

      {!boundary && readBoundary && (
        <div className="lm-map-actions">
          <button type="button" className="sx-cta sx-cta-2" disabled={saving} onClick={useReadBoundary}>Use the site boundary instead</button>
        </div>
      )}

      {showOptions && (
        <div className="lm-options" role="group" aria-label="Possible places for the site boundary">
          <div className="lm-options-nav">
            <button type="button" className="sx-cta sx-cta-2" disabled={saving || optionIdx === 0} onClick={() => chooseOption(optionIdx - 1)}>Back</button>
            <strong aria-live="polite">Option {optionIdx + 1} of {options.length}</strong>
            <button type="button" className="sx-cta sx-cta-2" disabled={saving || optionIdx >= options.length - 1} onClick={() => chooseOption(optionIdx + 1)}>Next</button>
          </div>
          <div className="sx-note">
            {optionIdx === 0
              ? 'Your location plan could fit the map in more than one place. This is the most likely, but use Next to see the others and pick the one that sits on the application site.'
              : 'Another place the location plan could fit the map. Use Back to return to the most likely, or Next to see more.'}
          </div>
        </div>
      )}

      {boundary && (
        <div className="lm-map-actions">
          <button type="button" className="sx-cta" disabled={!pos || saving} onClick={() => void confirm()}>
            {saving ? 'Saving…' : 'Use this site boundary'}
          </button>
          {boundary.userEdited && readBoundary && (
            <button type="button" className="sx-cta sx-cta-2" disabled={saving} onClick={useReadBoundary}>Reset to the calculated boundary</button>
          )}
          <button type="button" className="sx-cta sx-cta-2" disabled={saving} onClick={discardBoundary}>Use a pin and search area instead</button>
        </div>
      )}

      {mode === 'saved' && (
        <div className="lm-map-actions">
          <button type="button" className="sx-cta sx-cta-2" disabled={saving} onClick={findAgain}>Find the site again from the documents</button>
        </div>
      )}

      {plan && planCorners && (
        <div className="lm-controls">
          <div className="sx-field">
            <label className="lm-check">
              <input type="checkbox" checked={showPlan} onChange={e => setShowPlan(e.target.checked)} />
              Show the location plan on the map
            </label>
            {showPlan && (planError
              ? <div className="sx-err">The location plan could not be drawn on the map: {planError}</div>
              : planLoading
                ? <div className="sx-note">Drawing the location plan… <span className="skel" /></div>
                : (
                  <div className="lm-adjust-row">
                    <input
                      type="range" className="lm-range" aria-label="Location plan opacity" min={10} max={100} step={5}
                      value={Math.round(planOpacity * 100)} onChange={e => setPlanOpacity(Number(e.target.value) / 100)}
                    />
                    <span className="sx-note">{Math.round(planOpacity * 100)}% opaque</span>
                  </div>
                ))}
            <div className="sx-note">The plan follows the outline as you move, resize or turn it, so you can see how the two line up.</div>
          </div>
        </div>
      )}

      {boundary && pos && (() => {
        const adj = boundary.adjust ?? NO_ADJUST;
        const pct = Math.round(adj.scale * 100);
        const stated = result?.siteAreaHectares ?? null;
        const baseArea = boundary.areaHa / (adj.scale * adj.scale);
        const area = boundary.areaHa.toFixed(boundary.areaHa < 1 ? 2 : 1);
        return (
          <div className="lm-controls">
            <div className="sx-field">
              <label className="sx-label" htmlFor="lm-size">Size: {pct}% of the outline read from the plan · {area} ha</label>
              <div className="lm-adjust-row">
                <input
                  id="lm-size" type="range" className="lm-range" min={50} max={200} step={1}
                  value={Math.min(200, Math.max(50, pct))} disabled={saving}
                  onChange={e => applyAdjust({ ...adj, scale: Number(e.target.value) / 100 })}
                />
                <NumberField
                  label="Size as a percentage" min={Math.round(MIN_SCALE * 100)} max={Math.round(MAX_SCALE * 100)} step={1}
                  value={pct} disabled={saving} onCommit={v => applyAdjust({ ...adj, scale: v / 100 })}
                />
              </div>
              <div className="lm-adjust-actions">
                {stated != null && baseArea > 0 && (
                  <button type="button" className="sx-cta sx-cta-2" disabled={saving} onClick={() => applyAdjust({ ...adj, scale: Math.sqrt(stated / baseArea) })}>
                    Match the {stated} ha stated in the documents
                  </button>
                )}
                {Math.abs(adj.scale - 1) > 1e-6 && (
                  <button type="button" className="sx-cta sx-cta-2" disabled={saving} onClick={() => applyAdjust({ ...adj, scale: 1 })}>Reset size</button>
                )}
              </div>
            </div>

            <div className="sx-field">
              <label className="lm-check">
                <input type="checkbox" checked={rotationUnlocked} onChange={e => setRotationUnlocked(e.target.checked)} />
                Unlock rotation
              </label>
              <div className="sx-note">
                {Math.abs(adj.rotationDeg) > 1e-6
                  ? `Turned ${Math.abs(adj.rotationDeg).toFixed(1)}° ${adj.rotationDeg > 0 ? 'clockwise' : 'anticlockwise'} from north-up.`
                  : 'Plans are almost always drawn north-up, so rotation is locked until you need it.'}
              </div>
              {rotationUnlocked && (
                <>
                  <div className="lm-adjust-row">
                    <input
                      type="range" className="lm-range" aria-label="Rotation in degrees" min={-45} max={45} step={0.5}
                      value={Math.min(45, Math.max(-45, adj.rotationDeg))} disabled={saving}
                      onChange={e => applyAdjust({ ...adj, rotationDeg: Number(e.target.value) })}
                    />
                    <NumberField
                      label="Rotation in degrees, clockwise" min={-180} max={180} step={0.5}
                      value={Math.round(adj.rotationDeg * 10) / 10} disabled={saving} onCommit={v => applyAdjust({ ...adj, rotationDeg: v })}
                    />
                  </div>
                  {Math.abs(adj.rotationDeg) > 1e-6 && (
                    <div className="lm-adjust-actions">
                      <button type="button" className="sx-cta sx-cta-2" disabled={saving} onClick={() => applyAdjust({ ...adj, rotationDeg: 0 })}>Reset to north-up</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}

      <div className="lm-controls">
        {!boundary && <div className="sx-field">
          <label className="sx-label" htmlFor="lm-radius">Search area: about {radiusM} m around the pin</label>
          <input
            id="lm-radius" type="range" className="lm-range" min={MIN_RADIUS} max={Math.max(MAX_RADIUS, radiusM)} step={10} value={radiusM}
            disabled={!pos}
            onChange={e => { setRadiusM(Number(e.target.value)); setEdited(true); setTouched(true); }}
          />
          <div className="sx-note">A larger area catches more designations but is less precise about the site itself.</div>
        </div>}
      </div>

      <section>
        {basis.length > 0 || quotes.length > 0
          ? (
            <details className="lm-all">
              <summary>{locationHead}</summary>
              <ul className="lm-list">
                {basis.map((b, i) => <li key={`b${i}`}>{b}</li>)}
                {quotes.map((q, i) => <li key={`q${i}`} className="lm-quote">“{q}”</li>)}
              </ul>
            </details>
          )
          : <div className="lm-sum">{locationHead}</div>}
      </section>

      {boundary && (
        <section>
          <details className="lm-all">
            <summary>
              <div className="lm-sum">
                <h2 className="sx-sec-h">How we read the outline</h2>
                <span className={`sx-chip ${boundary.userEdited ? 'sx-chip-pos' : CONFIDENCE_CLASS[boundary.confidence]}`}>
                  {boundary.userEdited ? 'Outline adjusted by you' : `${boundary.confidence} confidence`} · {boundary.areaHa.toFixed(boundary.areaHa < 1 ? 2 : 1)} ha
                </span>
              </div>
            </summary>
            <ul className="lm-list">
              {boundary.documentName && <li>From “{boundary.documentName}”{boundary.scaleDenominator ? ` (scale 1:${boundary.scaleDenominator.toLocaleString('en-GB')})` : ''}.</li>}
              {boundary.evidence.map((e, i) => <li key={i}><strong>{e.label}</strong>{e.detail ? `: ${e.detail}` : ''}</li>)}
            </ul>
          </details>
          {boundary.warnings.length > 0 && <div className="lm-warnings" role="note">{boundary.warnings.map((w, i) => <div key={i}>{w}</div>)}</div>}
        </section>
      )}
      {!boundary && boundaryNote && <div className="sx-note">{boundaryNote}</div>}

      {warnings.length > 0 && (
        <div className="lm-warnings" role="note">
          {warnings.map((w, i) => <div key={i}>{w}</div>)}
        </div>
      )}

      {saveError && <div className="sx-err">{saveError}</div>}

      <div className="lm-actions">
        {!boundary && (
          <button type="button" className="sx-cta" disabled={!pos || saving} onClick={() => void confirm()}>
            {saving ? 'Saving…' : 'Confirm location'}
          </button>
        )}
        {cancelButton}
      </div>
    </div>
  );
}
